<script lang="ts" setup>
    import { ref, onMounted } from 'vue';
    import { api } from '@cwd/lib/main';
    import Modal from '@shared/components/Modal.vue';
    import MultiSelect from '@shared/components/MultiSelect/MultiSelect.vue';

    import BsAlert from '@shared/components/BsAlert.vue';
    import { AlertTypes, bsAlert } from '@shared/components/bsAlerts';

    interface AccountItem {
        AccountId: string;
        Name: string;
    }

    const props = defineProps<{
        show: boolean;
        journeyId: string;
        currentAccountId: string;
    }>();

    const emit = defineEmits<{
        close: [];
        copied: [accountNames: string[]];
    }>();

    const accounts = ref<AccountItem[]>([]);
    const selectedAccounts = ref<AccountItem[]>([]);
    const busy = ref(false);

    onMounted(async () => {
        const all = await api.get('/accounts') as AccountItem[];
        accounts.value = all.filter((a) => a.AccountId !== props.currentAccountId).sort((a, b) => a.Name.localeCompare(b.Name));
    });

    const copy = async () => {
        if (selectedAccounts.value.length === 0 || busy.value) return;
        busy.value = true;
        try {
            const targetAccountIds = selectedAccounts.value.map((a) => a.AccountId);
            await api.post(`/Journeys/CopyToAccount/${props.journeyId}`, targetAccountIds);
            emit('copied', selectedAccounts.value.map((a) => a.Name));
        } catch (err: any) {
            let errorMessage = $Resources.JourneyCopyToAccountErrorText;
            try {
                const parsed = JSON.parse(err?.message);
                if (parsed.Error === 'JourneyCopyToAccountNoAccess') {
                    errorMessage = String($Resources.JourneyCopyToAccountNoAccessError).replace('{AccountNames}', parsed.AccountNames.join(', '));
                }
            } catch {}
            bsAlert({ title: errorMessage, type: AlertTypes.Danger });
        } finally {
            busy.value = false;
        }
    };
</script>

<template>
    <Modal :title="$Resources.JourneyCopyToAccountModalTitle" :show="props.show" @close="emit('close')">
        <template #content>
            <BsAlert
                :type="AlertTypes.Info"
                :content="$Resources.JourneyCopyToAccountInfo"
                :dismissible="false" />

            <MultiSelect
                class="m-md-t"
                :items="accounts"
                :selectedItems="selectedAccounts"
                displayProp="Name"
                selectProp="AccountId"
                :label="$Resources.JourneyCopyToAccountSelectLabel"
                @update:selectedItems="(val) => (selectedAccounts = val)" />
        </template>
        <template #button>
            <button type="button" class="btn btn-primary" :disabled="selectedAccounts.length === 0 || busy" @click="copy">
                {{ $Resources.CopyButton }}
            </button>
        </template>
    </Modal>
</template>
